/**
 * Sprint 1 playback timing helpers — shared monotonic clock model for
 * manual, locked-focus, and video-synced playback paths.
 */

/** Clinical tolerance for playhead vs wall-clock at 1× (ms). Scales with |speed|. */
export const PLAYBACK_CLOCK_DRIFT_TOLERANCE_MS_AT_1X = 50;

/** Target frame budget for rAF-driven playback (60 Hz). */
export const PLAYBACK_RAF_FRAME_BUDGET_MS = 1000 / 60;

export interface PlaybackClockAnchor {
  wallMs: number;
  playheadSeconds: number;
  speed: number;
}

export interface PlaybackClockDriftSample {
  /** Expected playhead from wall clock since anchor. */
  expectedPlayheadSeconds: number;
  /** Actual playhead. */
  actualPlayheadSeconds: number;
  /** actual - expected, in seconds. */
  driftSeconds: number;
  /** |drift| in milliseconds of wall time at current speed. */
  driftMs: number;
  /** True when |driftMs| exceeds clinical tolerance * |speed|. */
  exceedsTolerance: boolean;
  toleranceMs: number;
}

export function resolvePlaybackFrameBudgetMs(params: {
  scheduler: "raf" | "timer";
  schedulerIntervalMs?: number;
}): number {
  if (params.scheduler === "timer") {
    const interval = Number(params.schedulerIntervalMs);
    if (Number.isFinite(interval) && interval > 0) {
      return interval;
    }
    return 100;
  }
  return PLAYBACK_RAF_FRAME_BUDGET_MS;
}

export function resolvePlaybackClockDriftToleranceMs(speed: number): number {
  const absoluteSpeed =
    typeof speed === "number" && Number.isFinite(speed)
      ? Math.max(1, Math.abs(speed))
      : 1;
  return PLAYBACK_CLOCK_DRIFT_TOLERANCE_MS_AT_1X * absoluteSpeed;
}

export function createPlaybackClockAnchor(params: {
  wallMs: number;
  playheadSeconds: number;
  speed: number;
}): PlaybackClockAnchor {
  return {
    wallMs: params.wallMs,
    playheadSeconds: params.playheadSeconds,
    speed: params.speed,
  };
}

/**
 * Compare anchored wall time + speed against the live playhead.
 * Positive driftMs means the playhead is ahead of the wall clock.
 */
export function measurePlaybackClockDrift(params: {
  anchor: PlaybackClockAnchor;
  nowMs: number;
  playheadSeconds: number;
  speed?: number;
}): PlaybackClockDriftSample {
  const speed =
    typeof params.speed === "number" && Number.isFinite(params.speed)
      ? params.speed
      : params.anchor.speed;
  const elapsedWallSec = Math.max(0, (params.nowMs - params.anchor.wallMs) / 1000);
  const expectedPlayheadSeconds =
    params.anchor.playheadSeconds + elapsedWallSec * speed;
  const driftSeconds = params.playheadSeconds - expectedPlayheadSeconds;
  const absoluteSpeed = Math.max(1e-6, Math.abs(speed));
  const driftMs = (Math.abs(driftSeconds) / absoluteSpeed) * 1000;
  const toleranceMs = resolvePlaybackClockDriftToleranceMs(speed);
  return {
    expectedPlayheadSeconds,
    actualPlayheadSeconds: params.playheadSeconds,
    driftSeconds,
    driftMs,
    exceedsTolerance: driftMs > toleranceMs,
    toleranceMs,
  };
}

export function resolveFrameBudgetRemainingMs(params: {
  tickWorkMs: number;
  frameBudgetMs: number;
}): number {
  const work = Number.isFinite(params.tickWorkMs)
    ? Math.max(0, params.tickWorkMs)
    : 0;
  const budget =
    Number.isFinite(params.frameBudgetMs) && params.frameBudgetMs > 0
      ? params.frameBudgetMs
      : PLAYBACK_RAF_FRAME_BUDGET_MS;
  return budget - work;
}
